import { v4 as uuid } from 'uuid'
import { cx } from 'class-variance-authority'
import { CheckIcon } from '@phosphor-icons/react/dist/ssr/Check'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { Button } from '@skop-app/lib-front'
import { AdaptedProduct } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'

import { useUserContext } from '@/context/UserContext'
import { useCreateReview } from '@/services/mutations/review'
import { useReviews } from '@/services/queries'

type AlertReviewCardProps = {
  queryKey: string[] // tanstack query key to update cache more easily
  product: AdaptedProduct
  children?: React.ReactNode // ProductCard component for now
}

/**
 * Wrapper for ProductCard with review actions, could be used in a grid or list
 * @param {AlertReviewCardProps} props
 * @returns {React.ReactNode}
 * @example in app/(restricted)/flux/results/page.tsx
 **/

export const AlertReviewCard = ({
  queryKey,
  product,
  children,
}: AlertReviewCardProps): React.ReactNode => {
  return (
    <div className="group relative h-full">
      {children}
      <ProductReviewActions
        queryKey={queryKey}
        product={product}
        className="opacity-0 group-hover:opacity-100"
      />
    </div>
  )
}

const ProductReviewActions = ({ queryKey, product, className }) => {
  const { ownership } = useUserContext()
  const { mutate: createReview } = useCreateReview()

  const { byUserID, organizationID } = ownership || {}
  const { productID } = product

  // Fetch only alert reviews for this elementID
  const { data: alertReviews = [] } = useReviews({
    type: { eq: 'ALERT' },
    elementID: { eq: productID },
  })
  const currentReview = alertReviews?.find(
    (review) =>
      review.type === 'ALERT' &&
      (review.byUserID === byUserID ||
        review.organizationID === organizationID),
  )

  const handleAlertReview = (status: ReviewStatus) => {
    if (!byUserID) return console.error('No user ID')
    createReview({
      id: uuid(),
      type: ReviewType.ALERT,
      status,
      elementID: productID,
      byUserID,
      organizationID: organizationID as string,
      queryKey,
    })
  }

  if (currentReview?.status === ReviewStatus.ACCEPTED) {
    return (
      <div className="absolute right-2 top-2 rounded-sm bg-background p-2">
        <CheckIcon className="size-5 text-green-500" />
      </div>
    )
  }
  if (currentReview?.status === ReviewStatus.REJECTED) {
    return (
      <div className="absolute right-2 top-2 rounded-sm bg-background p-2">
        <XIcon className="size-5 text-red-500" />
      </div>
    )
  }

  return (
    <div
      className={cx(
        'absolute right-2 top-2 flex translate-x-2 transform space-x-1 transition-all duration-300 group-hover:translate-x-0',
        className,
      )}
    >
      <Button
        variant="action"
        title="Refuser"
        className="group/btn"
        onClick={() => handleAlertReview(ReviewStatus.REJECTED)}
      >
        <XIcon className="size-5 group-hover/btn:text-red-500" />
      </Button>
      <Button
        variant="action"
        title="Accepter"
        className="group/btn"
        onClick={() => handleAlertReview(ReviewStatus.ACCEPTED)}
      >
        <CheckIcon className="size-5 group-hover/btn:text-green-500" />
      </Button>
    </div>
  )
}
